import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Field,
  Input,
  Switch,
  MessageBar,
  MessageBarBody,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { ClientTaxRateConfig } from '../../types';
import {
  useClientTaxRateConfig,
  useUpsertClientTaxRateConfig,
} from '../../services/complianceApi';
import { fractionToPercentInput, parseRatePercent, percentToFraction } from '../../utils/surtaxEdit';

const useStyles = makeStyles({
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
});

/**
 * The client-level rate config, reached from a task page.
 *
 * 🔴 The override is entered as a PERCENT (1.5 = 1.5%) and stored as a fraction. It used to be
 * entered as a fraction ("e.g. 0.01 = 1%"), while the server's validation — added with the
 * surtax allocation work — answers an out-of-range value with "enter 1.5 for 1.5%, not 0.015".
 * A staff member who followed that instruction here typed 1.5, which meant 150%, and was refused
 * with the same sentence again. One unit everywhere: the one the server's message names.
 */
interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  onSaved?: () => void;
}

export default function ClientTaxRateConfigDialog({ open, onOpenChange, clientId, onSaved }: Props) {
  const styles = useStyles();
  const { t } = useTranslation();

  const { data } = useClientTaxRateConfig(clientId);
  const upsert = useUpsertClientTaxRateConfig();

  const [county, setCounty] = useState('');
  const [surtaxOverride, setSurtaxOverride] = useState('');
  const [allowanceEligible, setAllowanceEligible] = useState(true);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Seed form when dialog opens or data arrives.
  useEffect(() => {
    setCounty(data?.county ?? '');
    setSurtaxOverride(fractionToPercentInput(data?.countySurtaxRateOverride));
    setAllowanceEligible(data?.collectionAllowanceEligible ?? true);
    setSubmitError(null);
    setValidationError(null);
  }, [data, open]);

  function handleSave() {
    const parsed = parseRatePercent(surtaxOverride);
    if (!parsed.ok) {
      setValidationError(t(`surtaxEdit.validation.${parsed.error}`));
      return;
    }
    const override = parsed.value === null ? null : percentToFraction(parsed.value);
    setValidationError(null);
    setSubmitError(null);

    const payload: ClientTaxRateConfig = {
      clientId,
      county: county.trim() || undefined,
      stateRate: data?.stateRate ?? 0.06,
      countySurtaxRateOverride: override,
      collectionAllowanceEligible: allowanceEligible,
    };

    upsert.mutate(
      { clientId, payload },
      {
        onSuccess: () => {
          onSaved?.();
          onOpenChange(false);
        },
        onError: (err) =>
          setSubmitError(
            err instanceof Error ? err.message : t('rateConfigDialog.saveFailed'),
          ),
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(_e, d) => {
        if (!upsert.isPending) onOpenChange(d.open);
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('rateConfigDialog.title')}</DialogTitle>
          <DialogContent>
            <div className={styles.form}>
              {submitError && (
                <MessageBar intent="error">
                  <MessageBarBody>{submitError}</MessageBarBody>
                </MessageBar>
              )}

              <Field label={t('rateConfigDialog.county')}>
                <Input
                  value={county}
                  onChange={(_e, d) => setCounty(d.value)}
                  disabled={upsert.isPending}
                />
              </Field>

              <Field
                label={t('rateConfigDialog.surtaxOverride')}
                hint={t('rateConfigDialog.surtaxOverrideHint')}
                validationMessage={validationError ?? undefined}
                validationState={validationError ? 'error' : 'none'}
              >
                <Input
                  inputMode="decimal"
                  value={surtaxOverride}
                  onChange={(_e, d) => {
                    setSurtaxOverride(d.value);
                    setValidationError(null);
                  }}
                  placeholder={t('rateConfigDialog.surtaxPlaceholder')}
                  disabled={upsert.isPending}
                />
              </Field>

              <Field label={t('rateConfigDialog.allowanceEligible')}>
                <Switch
                  checked={allowanceEligible}
                  onChange={(_e, d) => setAllowanceEligible(d.checked)}
                  disabled={upsert.isPending}
                />
              </Field>
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              disabled={upsert.isPending}
              onClick={() => onOpenChange(false)}
            >
              {t('rateConfigDialog.cancel')}
            </Button>
            <Button
              appearance="primary"
              disabled={upsert.isPending}
              icon={upsert.isPending ? <Spinner size="tiny" /> : undefined}
              onClick={handleSave}
            >
              {t('rateConfigDialog.save')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
